// src/pages/ResetPasswordPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import useScrollToTop from "./../../hooks/useScollToTop";
import {
  checkResetPasswordToken,
  resetPassword,
} from "./../../services/passwordServices";
import { getSettings } from "./../../services/settingsServices";
import {
  HiOutlineLockClosed,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineArrowLeft,
} from "react-icons/hi";

/* ========= Helpers identiques ErrorPage/Login/Register ========= */
function toAbsolute(u = "") {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith("/")) return `${window.location.origin}${u}`;
  return u;
}
function pickIndex(n, exclude = -1) {
  if (n <= 1) return 0;
  let idx = Math.floor(Math.random() * n);
  if (idx === exclude) idx = (idx + 1) % n;
  return idx;
}
/* =============================================================== */

export default function ResetPasswordPage() {
  useScrollToTop();
  const navigate = useNavigate();
  const { token } = useParams();

  const [bgUrl, setBgUrl] = useState(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPwd, setShowPwd] = useState(false);
  const [showConfirmPwd, setShowConfirmPwd] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkingToken, setCheckingToken] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  // ✅ Fond dynamique comme les autres pages auth
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getSettings();
        const s = Array.isArray(data) ? data[0] : data;

        const raw = [s?.photoURL1, s?.photoURL2, s?.photoURL3].filter(
          (u) => typeof u === "string" && u.trim().length > 0
        );
        const unique = Array.from(new Set(raw));
        const candidates = unique.map(toAbsolute);

        if (!mounted) return;

        if (candidates.length > 0) {
          const prev = Number(sessionStorage.getItem("resetBgIdx"));
          const idx = pickIndex(
            candidates.length,
            Number.isNaN(prev) ? -1 : prev
          );
          sessionStorage.setItem("resetBgIdx", String(idx));
          setBgUrl(candidates[idx]);
        } else {
          setBgUrl(null);
        }
      } catch (e) {
        console.error("ResetPasswordPage getSettings:", e);
        if (mounted) setBgUrl(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // ✅ Vérification du token au chargement
  useEffect(() => {
    const verifyToken = async () => {
      setCheckingToken(true);
      setError("");
      setSuccess("");

      try {
        const result = await checkResetPasswordToken(token);
        if (!result?.valid) {
          setTokenValid(false);
          setError(
            result?.message || "Token de réinitialisation invalide ou expiré."
          );
        } else {
          setTokenValid(true);
        }
      } catch (e) {
        console.error("checkResetPasswordToken error:", e);
        setTokenValid(false);
        setError(
          "Impossible de vérifier le lien. Merci de demander une nouvelle réinitialisation."
        );
      } finally {
        setCheckingToken(false);
      }
    };

    if (token) {
      verifyToken();
    } else {
      setTokenValid(false);
      setCheckingToken(false);
      setError("Lien de réinitialisation invalide.");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!tokenValid) return;

    setError("");
    setSuccess("");

    if (!password || !confirmPassword) {
      setError("Merci de saisir et confirmer votre nouveau mot de passe.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);
    try {
      const result = await resetPassword(token, password);

      if (result?.success) {
        setSuccess(
          result?.message ||
            "Votre mot de passe a été réinitialisé avec succès."
        );
        setPassword("");
        setConfirmPassword("");

        // Redirection douce vers la page de login
        setTimeout(() => navigate("/login"), 3500);
      } else {
        setError(
          result?.message ||
            "Impossible de réinitialiser le mot de passe. Merci de réessayer."
        );
      }
    } catch (e) {
      console.error("resetPassword error:", e);
      setError(
        "Une erreur interne est survenue. Merci de réessayer dans quelques instants."
      );
    } finally {
      setLoading(false);
    }
  };

  const disableForm = checkingToken || !tokenValid || loading;

  return (
    <div className="relative min-h-screen">
      {/* Fond image ou dégradé */}
      <div
        className={`absolute inset-0 ${
          bgUrl
            ? "bg-cover bg-center bg-no-repeat"
            : "bg-gradient-to-b from-slate-900 to-slate-700"
        }`}
        style={bgUrl ? { backgroundImage: `url(${bgUrl})` } : undefined}
        aria-hidden="true"
      />
      {/* Voile */}
      <div
        className="absolute inset-0 bg-slate-900/50 dark:bg-slate-950/60"
        aria-hidden="true"
      />

      {/* Contenu */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-4 text-center text-slate-100">
            <span className="inline-flex items-center px-3 py-1 rounded-full bg-white/10 text-xs uppercase tracking-wide border border-white/20 mb-3">
              Sécurité du compte
            </span>
            <h1 className="text-3xl font-bold drop-shadow-sm">
              Nouveau mot de passe
            </h1>
            <p className="mt-1 text-sm text-slate-200/90">
              Choisissez un mot de passe sécurisé pour votre compte.
            </p>
          </div>

          {/* Carte */}
          <div className="bg-white/80 dark:bg-slate-900/90 backdrop-blur-md rounded-2xl shadow-xl border border-white/40 dark:border-slate-800/70 p-6 md:p-7 space-y-4">
            {/* Messages */}
            {error && (
              <div
                className="rounded-xl border border-red-200 bg-red-50/90 text-red-800 text-sm px-3 py-2 flex items-start gap-2 dark:bg-red-900/40 dark:border-red-800 dark:text-red-100"
                role="alert"
              >
                <span className="mt-0.5">⚠️</span>
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div
                className="rounded-xl border border-emerald-200 bg-emerald-50/90 text-emerald-800 text-sm px-3 py-2 flex items-start gap-2 dark:bg-emerald-900/40 dark:border-emerald-800 dark:text-emerald-100"
                role="alert"
              >
                <span className="mt-0.5">✅</span>
                <span>
                  {success}
                  <br />
                  <span className="text-xs opacity-80">
                    Redirection vers la page de connexion dans quelques
                    secondes…
                  </span>
                </span>
              </div>
            )}

            {checkingToken ? (
              <div className="flex flex-col items-center justify-center py-6 gap-3 text-sm text-slate-500 dark:text-slate-300">
                <span className="inline-flex h-9 w-9 rounded-full border-2 border-slate-300 border-t-transparent animate-spin" />
                <span>Vérification du lien de réinitialisation…</span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Nouveau mot de passe */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Nouveau mot de passe
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-3 flex items-center text-slate-400">
                      <HiOutlineLockClosed className="h-5 w-5" />
                    </span>
                    <input
                      type={showPwd ? "text" : "password"}
                      className="mt-0 block w-full pl-10 pr-10 py-2.5 bg-white/90 border border-slate-300 rounded-xl text-sm shadow-sm placeholder-slate-400
                                 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/60
                                 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                      placeholder="Votre nouveau mot de passe"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={disableForm}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd((v) => !v)}
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      tabIndex={-1}
                    >
                      {showPwd ? (
                        <HiOutlineEyeOff className="h-5 w-5" />
                      ) : (
                        <HiOutlineEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirmation */}
                <div className="space-y-1.5">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Confirmer le mot de passe
                  </label>
                  <div className="relative">
                    <span className="absolute inset-y-0 left-3 flex items-center text-slate-400">
                      <HiOutlineLockClosed className="h-5 w-5" />
                    </span>
                    <input
                      type={showConfirmPwd ? "text" : "password"}
                      className="mt-0 block w-full pl-10 pr-10 py-2.5 bg-white/90 border border-slate-300 rounded-xl text-sm shadow-sm placeholder-slate-400
                                 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/60
                                 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500"
                      placeholder="Confirmez votre mot de passe"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={disableForm}
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPwd((v) => !v)}
                      className="absolute inset-y-0 right-3 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      tabIndex={-1}
                    >
                      {showConfirmPwd ? (
                        <HiOutlineEyeOff className="h-5 w-5" />
                      ) : (
                        <HiOutlineEye className="h-5 w-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Indication sécurité */}
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  Utilisez un mot de passe d’au moins 8 caractères, avec des
                  lettres, chiffres et caractères spéciaux pour améliorer la
                  sécurité.
                </div>

                {/* Bouton submit */}
                <button
                  type="submit"
                  disabled={disableForm}
                  className={`w-full px-4 py-2.5 rounded-xl shadow-sm border text-sm font-medium flex items-center justify-center gap-2
                    transition-transform duration-150
                    ${
                      disableForm
                        ? "bg-slate-400 text-white cursor-not-allowed border-slate-300"
                        : "bg-primary text-white border-primary hover:bg-white hover:text-primary transform hover:-translate-y-0.5 hover:shadow-md"
                    }`}
                >
                  {loading && (
                    <span className="inline-flex h-4 w-4 rounded-full border-2 border-white/60 border-t-transparent animate-spin" />
                  )}
                  <span>Réinitialiser le mot de passe</span>
                </button>

                {/* Lien pour redemander un token si invalide */}
                {!tokenValid && !checkingToken && (
                  <div className="mt-2 text-xs text-slate-500 dark:text-slate-400 text-center">
                    Le lien semble invalide ou expiré.{" "}
                    <Link
                      to="/forgot-password"
                      className="underline underline-offset-2"
                    >
                      Demander un nouveau lien
                    </Link>
                    .
                  </div>
                )}

                {/* Lien retour */}
                <div className="flex items-center justify-center gap-2 pt-2 text-xs text-slate-500 dark:text-slate-400">
                  <HiOutlineArrowLeft className="h-4 w-4" />
                  <Link
                    to="/login"
                    className="underline-offset-2 hover:underline font-medium"
                  >
                    Retour à la page de connexion
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
